import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Check, ArrowLeft } from 'lucide-react';
import { servicesAPI } from '../services/api';

const ServiceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedServices, setRelatedServices] = useState([]);

  useEffect(() => {
    const fetchService = async () => {
      try {
        setLoading(true);
        const serviceData = await servicesAPI.getBySlug(slug);
        setService(serviceData);
        
        // Fetch all services for "related services" section
        const allServices = await servicesAPI.getAll();
        setRelatedServices(allServices.filter(s => s.slug !== slug).slice(0, 3));
      } catch (err) {
        console.error('Error fetching service:', err);
        setError('Услуга не найдена');
      } finally {
        setLoading(false);
      }
    };

    fetchService();
  }, [slug]);

  if (loading) {
    return (
      <div className="container" style={{ padding: 'var(--space-xxxl) var(--space-l)', textAlign: 'center' }}>
        <p>Загрузка...</p>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="container" style={{ padding: 'var(--space-xxxl) var(--space-l)', textAlign: 'center' }}>
        <h2>Услуга не найдена</h2>
        <Link to="/services">
          <button className="btn-primary" style={{ marginTop: 'var(--space-l)' }}>
            <span>Все услуги</span>
          </button>
        </Link>
      </div>
    );
  }

  const scrollToContact = () => {
    navigate({ pathname: '/', hash: '#contact' });
  };

  return (
    <div>
      {/* Hero */}
      <section style={{
        position: 'relative',
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        background: `linear-gradient(135deg, rgba(212, 196, 176, 0.15) 0%, rgba(250, 249, 247, 1) 100%)`,
        padding: 'var(--space-xxxl) var(--space-l)'
      }}>
        <div className="container">
          <Link to="/services" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-s)',
            color: 'var(--warm-gray)',
            textDecoration: 'none',
            marginBottom: 'var(--space-l)',
            fontSize: '0.9375rem',
            transition: 'color 300ms ease'
          }}
          className="back-link">
            <ArrowLeft size={18} />
            Все услуги
          </Link>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 'var(--space-xxl)',
            alignItems: 'center'
          }}>
            <div style={{ maxWidth: '600px' }}>
              <h1 style={{ marginBottom: 'var(--space-l)' }}>
                {service.title}
              </h1>
              <p style={{
                fontSize: '1.125rem',
                color: 'var(--warm-gray)',
                marginBottom: 'var(--space-xl)',
                lineHeight: '1.7'
              }}>
                {service.description}
              </p>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'var(--space-m)',
                marginBottom: 'var(--space-xl)'
              }}>
                <div style={{
                  padding: 'var(--space-m) var(--space-l)',
                  backgroundColor: 'var(--soft-gray)',
                  borderRadius: 'var(--radius-sm)'
                }}>
                  <p className="caption" style={{ color: 'var(--warm-sage)', marginBottom: 'var(--space-xs)' }}>
                    Длительность
                  </p>
                  <p style={{ color: 'var(--charcoal)', fontWeight: 500 }}>
                    {service.duration}
                  </p>
                </div>
                <div style={{
                  padding: 'var(--space-m) var(--space-l)',
                  backgroundColor: 'var(--soft-gray)',
                  borderRadius: 'var(--radius-sm)'
                }}>
                  <p className="caption" style={{ color: 'var(--warm-sage)', marginBottom: 'var(--space-xs)' }}>
                    Стоимость
                  </p>
                  <p style={{
                    color: 'var(--charcoal)',
                    fontWeight: 500,
                    fontFamily: 'var(--font-primary)',
                    fontSize: '1.125rem'
                  }}>
                    {service.price}
                  </p>
                </div>
              </div>
              <button className="btn-primary" onClick={scrollToContact}>
                Записаться на консультацию
              </button>
            </div>
            <div style={{
              position: 'relative',
              height: '500px',
              borderRadius: 'var(--radius-md)',
              overflow: 'hidden',
              boxShadow: 'var(--shadow-medium)'
            }}>
              <img
                src={service.image_url}
                alt={service.title}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Details */}
      <section className="section">
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 'var(--space-xxl)'
          }}>
            <div>
              <h2 style={{ marginBottom: 'var(--space-l)' }}>Что вы получите</h2>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {service.result.map((item, index) => (
                  <li key={index} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 'var(--space-m)',
                    marginBottom: 'var(--space-l)',
                    color: 'var(--warm-gray)',
                    fontSize: '1.0625rem',
                    lineHeight: '1.6'
                  }}>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--warm-sage)',
                      color: 'var(--cream-white)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      marginTop: '2px'
                    }}>
                      <Check size={14} />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="card" style={{
              padding: 'var(--space-xl)',
              backgroundColor: 'var(--soft-gray)',
              height: 'fit-content'
            }}>
              <h3 style={{ marginBottom: 'var(--space-l)' }}>Формат работы</h3>
              <p style={{
                color: 'var(--warm-gray)',
                marginBottom: 'var(--space-xl)',
                fontSize: '1.0625rem',
                lineHeight: '1.6'
              }}>
                {service.format}
              </p>
              <div style={{
                padding: 'var(--space-l)',
                backgroundColor: 'var(--cream-white)',
                borderRadius: 'var(--radius-sm)',
                marginBottom: 'var(--space-l)'
              }}>
                <p style={{
                  fontFamily: 'var(--font-primary)',
                  fontSize: '2rem',
                  color: 'var(--warm-sage)',
                  marginBottom: 'var(--space-s)',
                  textAlign: 'center'
                }}>
                  {service.price}
                </p>
                <p style={{
                  textAlign: 'center',
                  color: 'var(--warm-gray)',
                  fontSize: '0.875rem'
                }}>
                  Точная стоимость обсуждается индивидуально
                </p>
              </div>
              <button className="btn-primary" onClick={scrollToContact} style={{ width: '100%' }}>
                Оставить заявку
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="section section-bg-gray">
        <div className="container">
          <h2 style={{ marginBottom: 'var(--space-xxl)', textAlign: 'center' }}>
            Другие услуги
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 'var(--space-xl)'
          }}>
            {relatedServices.map((relatedService) => (
              <Link
                key={relatedService.id}
                to={`/services/${relatedService.slug}`}
                style={{ textDecoration: 'none' }}
              >
                <div className="card" style={{ height: '100%' }}>
                  <div style={{
                    height: '180px',
                    overflow: 'hidden',
                    marginBottom: 'var(--space-l)'
                  }}>
                    <img
                      src={relatedService.image_url}
                      alt={relatedService.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </div>
                  <h3 style={{
                    marginBottom: 'var(--space-m)',
                    color: 'var(--charcoal)',
                    fontSize: '1.125rem'
                  }}>
                    {relatedService.title}
                  </h3>
                  <p style={{
                    color: 'var(--warm-gray)',
                    fontSize: '0.9375rem'
                  }}>
                    {relatedService.short_description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <style>{`
        .back-link:hover {
          color: var(--warm-sage);
        }
      `}</style>
    </div>
  );
};

export default ServiceDetail;
